/**
 * Algolia's per-index typo tolerance as Typesense search parameters.
 *
 * Algolia sets typo tolerance on the index; Typesense takes it per search, so
 * each Typesense request picks the profile of the Algolia index it replaces.
 * Values are from docs/algolia-index-settings-export.md.
 *
 * Not ported: `disableTypoToleranceOnWords` (`essen`, `kerzen` on events and
 * kits) has no Typesense equivalent. Typesense's default
 * `typo_tokens_threshold: 1` only tries typo variants when a word has no exact
 * matches, and both words have plenty, so they already match exactly.
 */

export interface TypoProfile {
  /** Algolia `minWordSizefor1Typo`. */
  minLen1Typo: number
  /** Algolia `minWordSizefor2Typos`. */
  minLen2Typo: number
  /** Algolia `disableTypoToleranceOnAttributes`. */
  noTypoFields?: string[]
}

export const TYPO_PROFILES = {
  /** `events_de` / `events_en` primary: the relevancy sort and the dropdown. */
  events: { minLen1Typo: 5, minLen2Typo: 9, noTypoFields: ['city'] },
  /** `events_de` sort replicas: the newest and price sorts. */
  eventSortReplicas: { minLen1Typo: 3, minLen2Typo: 6 },
  categories: { minLen1Typo: 4, minLen2Typo: 8 },
  locations: { minLen1Typo: 4, minLen2Typo: 8 },
  products: { minLen1Typo: 4, minLen2Typo: 8 },
} satisfies Record<string, TypoProfile>

/**
 * Search params for `profile`. Typesense sets `num_typos` per `query_by` field
 * as a list in the same order, so it's derived from the request's own
 * `query_by` and stays aligned when fields change.
 */
export const typesenseTypoParams = (
  queryBy: string,
  { minLen1Typo, minLen2Typo, noTypoFields = [] }: TypoProfile
): { min_len_1typo: number; min_len_2typo: number; num_typos?: string } => ({
  min_len_1typo: minLen1Typo,
  min_len_2typo: minLen2Typo,
  ...(noTypoFields.length
    ? {
        num_typos: queryBy
          .split(',')
          .map((field) => (noTypoFields.includes(field.trim()) ? 0 : 2))
          .join(','),
      }
    : {}),
})
